import 'react-confirm-alert/src/react-confirm-alert.css';
import 'react-tooltip/dist/react-tooltip.css';

import { Add, Remove } from '@mui/icons-material';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import * as React from 'react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

import { useShoppingList } from '../context/ShoppingListContext';
import { Product, Tag } from '../models/Product';
import ProudctAddedToList from './modals/productAddedToList/ProudctAddedToList';
import translateTag from '../helperFunctions/translateTag';
import { useConfig } from '../utils/useConfig';

interface ProductCardProps {
  product: Product;
  vertical?: boolean;
  className?: string;
}

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  vertical = false,
  className = '',
}) => {
  const config = useConfig();
  const [productsNumber, setProductsNumber] = useState(1);
  const [isHovered, setIsHovered] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const { addToShoppingList } = useShoppingList();

  const getTagColor = (tag?: string | null) => {
    switch (tag) {
      case 'New':
        return `bg-${config.theme.mainColor}-50 text-${config.theme.mainColor}-700 border-${config.theme.mainColor}-200`;
      case 'Sale':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'Bestseller':
        return 'bg-red-50 text-red-700 border-red-200';
      case 'Recommended':
        return 'bg-amber-50 text-amber-700 border-amber-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  // Sprawdzenie czy produkt ma aktywną zniżkę
  const hasDiscount =
    product.discount &&
    product.discount.isActive &&
    product.lowestPriceLast30Days;

  return (
    <motion.div
      className={`
        w-full relative overflow-hidden bg-white border border-gray-100 rounded-lg shadow-sm
        ${vertical ? 'flex flex-col' : 'flex flex-row'}
        ${className || ''}
      `}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      whileHover={{
        boxShadow:
          '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.05)',
        translateY: -4,
      }}
      data-tooltip-id="my-tooltip"
      data-tooltip-content={window.innerWidth > 768 ? product.name : ''}
    >
      {/* Tag */}
      {product.tag && product.tag !== Tag.New && (
        <div className="absolute top-3 left-3 z-10">
          <div
            className={`text-xs px-2 py-1 rounded-md border ${getTagColor(product.tag)}`}
          >
            {translateTag(product.tag)}
          </div>
        </div>
      )}

      {/* Discount Label */}
      {hasDiscount && (
        <div className="absolute top-3 right-3 z-10">
          <div className="text-xs px-2 py-1 rounded-md font-medium text-white bg-purple-600">
            {product.discount?.type === 'percentage'
              ? `- ${product.discount.value}%`
              : `- ${product.discount?.value.toFixed(2)} zł`}
          </div>
        </div>
      )}

      {/* Image Section */}
      <Link
        to={`/produkt/${product.slug}`}
        className={`
          relative overflow-hidden 
          ${vertical ? 'w-full aspect-square' : 'w-32 h-32 m-4'}
        `}
      >
        <motion.div
          className="w-full h-full"
          whileHover={{ scale: 1.05 }}
          transition={{ duration: 0.3 }}
        >
          <motion.img
            src={product.mobilePrimaryImage}
            alt={product.name}
            className={`
              object-contain object-center w-full h-full
              transition-transform duration-300
            `}
            initial={{ opacity: 0.8 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          />
        </motion.div>
      </Link>

      {/* Content Section */}
      <div
        className={`
        flex flex-col 
        ${vertical ? 'p-4 flex-grow' : 'flex-grow p-4 pl-0'}
      `}
      >
        {/* Product Name */}
        <Link to={`/produkt/${product.slug}`} className="group mb-2">
          <h3
            className={`text-gray-800 font-medium text-sm sm:text-base group-hover:text-${config.theme.mainColor}-600 transition-colors duration-200`}
          >
            {product.name.length > 40
              ? `${product.name.slice(0, 40)}...`
              : product.name}
          </h3>
          <div
            className={`w-0 group-hover:w-full h-[1px] bg-${config.theme.mainColor}-500 transition-all duration-300`}
          ></div>
        </Link>

        {/* Price Section - Zmodyfikowana sekcja ceny z uwzględnieniem zniżek */}
        <div className="mb-3">
          {hasDiscount ? (
            <>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-bold text-gray-900">
                  {product.price.toFixed(2)} zł
                </span>
                <span className="text-sm text-gray-500 line-through">
                  {product.lowestPriceLast30Days?.toFixed(2)} zł
                </span>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Netto: {(product.price * 0.77).toFixed(2)} zł
              </div>
              <div className="text-xs text-gray-600 mt-1 bg-gray-50 px-1 py-0.5 rounded w-fit">
                Najniższa cena: {product.lowestPriceLast30Days?.toFixed(2)} zł
              </div>
            </>
          ) : (
            <div className="flex items-baseline gap-2">
              <span className="font-bold text-gray-900">
                {product.price.toFixed(2)} zł
              </span>
              <span className="text-xs text-gray-500">
                Netto: {(product.price * 0.77).toFixed(2)} zł
              </span>
            </div>
          )}
        </div>

        {/* Add to Cart Controls */}
        <motion.div
          className={`
            flex items-center gap-2 mt-auto 
            ${vertical ? 'flex-col' : 'flex-row justify-between'}
          `}
          initial={{ opacity: 0, y: 10 }}
          animate={{
            opacity: vertical || isHovered ? 1 : 0.8,
            y: vertical || isHovered ? 0 : 10,
          }}
          transition={{ duration: 0.2 }}
        >
          <div className="flex items-center gap-1 bg-gray-50 rounded-full px-2 py-1">
            <motion.button
              className="w-6 h-6 rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-200 transition-colors"
              whileTap={{ scale: 0.9 }}
              onClick={() => {
                if (productsNumber > 1) {
                  setProductsNumber(productsNumber - 1);
                }
              }}
            >
              <Remove fontSize="small" />
            </motion.button>
            <span className="w-6 text-center font-medium text-sm">
              {productsNumber}
            </span>
            <motion.button
              className="w-6 h-6 rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-200 transition-colors"
              whileTap={{ scale: 0.9 }}
              onClick={() => {
                if (productsNumber < 99) {
                  setProductsNumber(productsNumber + 1);
                }
              }}
            >
              <Add fontSize="small" />
            </motion.button>
          </div>

          <motion.button
            className={`
              flex items-center gap-2 bg-gray-900 hover:bg-gray-800 text-white rounded-md transition-colors
              ${vertical ? 'w-full py-2 justify-center' : 'px-4 py-2'}
            `}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => {
              addToShoppingList(product, productsNumber);
              setIsModalVisible(true);
            }}
          >
            <ShoppingBagIcon fontSize="small" />
            {vertical && <span className="text-sm">Dodaj do koszyka</span>}
          </motion.button>
        </motion.div>
      </div>

      <ProudctAddedToList
        isModalVisible={isModalVisible}
        setIsModalVisible={setIsModalVisible}
        product={product}
        quantity={productsNumber}
      />
    </motion.div>
  );
};

export default ProductCard;
